/**
 * Camera field-of-view, aspect-compensated ("Hor+") for narrow portrait phones.
 *
 * THE PROBLEM (owner / camera diagnosis): the chase camera uses a FIXED VERTICAL fov (58°). three.js keeps the
 * vertical fov constant and derives the horizontal fov from the aspect ratio, so on a tall portrait phone the
 * HORIZONTAL fov collapses:
 *   • desktop 16:9 (aspect 1.78): hfov ≈ 89°  — you see plenty of sea left/right
 *   • iPhone portrait 390×844 (aspect 0.46): hfov ≈ 29° — a claustrophobic slot, the mobile "I can't see" report
 *
 * THE FIX: on narrow screens RAISE the vertical fov just enough to hold a decent HORIZONTAL fov (the "Hor+"
 * lens model most mobile games use), capped so it never fisheyes. Landscape / desktop are left exactly as
 * authored. Pure (aspect in, vertical-fov out) so it unit-tests with no WebGL.
 */

export interface CamFovCfg {
  /** The authored vertical fov (degrees) — used as-is on landscape/desktop, the floor everywhere. */
  baseVFov: number;
  /** Horizontal fov (degrees) we try to preserve on narrow screens (never more than desktop already gives). */
  targetHFov: number;
  /** Hard cap on the vertical fov (degrees) so a very tall phone can't fisheye. */
  maxVFov: number;
}

export const CAM_FOV: CamFovCfg = {
  baseVFov: 58,
  targetHFov: 52,
  maxVFov: 80,
};

const DEG = Math.PI / 180;

/** Horizontal fov (degrees) that a given vertical fov + aspect produce (three.js lens math). Pure. */
export function horizontalFov(vFovDeg: number, aspect: number): number {
  return (2 * Math.atan(Math.tan((vFovDeg * DEG) / 2) * aspect)) / DEG;
}

/**
 * The vertical fov (degrees) to hand three.js for this aspect. Returns `baseVFov` on landscape/desktop; on
 * narrow (portrait) screens raises it toward the fov that yields `targetHFov` horizontally, clamped to
 * `maxVFov`. Monotonic and continuous across the aspect where the base fov already meets the target, so there
 * is no visible pop when a device rotates. Pure.
 */
export function verticalFovForAspect(aspect: number, cfg: CamFovCfg = CAM_FOV): number {
  if (!(aspect > 0) || !Number.isFinite(aspect)) return cfg.baseVFov;
  // The horizontal fov we aim to keep: our target, but never wider than what the base fov gives at this aspect
  // (so we only ever ADD fov on narrow screens — landscape/desktop keep the authored lens untouched).
  const baseH = horizontalFov(cfg.baseVFov, aspect);
  if (baseH >= cfg.targetHFov) return cfg.baseVFov; // wide enough already — leave it alone
  // Raise the vertical fov so the horizontal fov climbs back to the target, capped against fisheye.
  const needV = (2 * Math.atan(Math.tan((cfg.targetHFov * DEG) / 2) / aspect)) / DEG;
  return Math.min(cfg.maxVFov, Math.max(cfg.baseVFov, needV));
}
